<script setup>
import { computed } from "vue"

const route = useRoute()
const apiBase = useApiBase()
const apiUrl = apiBase
  ? `${apiBase}/api/projects/${route.params.slug}/`
  : `/api/projects/${route.params.slug}/`

const { data, error } = await useFetch(apiUrl, {
  key: `project-${route.params.slug}`,
})

if (error.value) {
  throw createError({
    statusCode: error.value.statusCode || 500,
    statusMessage: error.value.statusMessage || "Failed to load project",
  })
}

const project = computed(() => data.value?.project || null)
const seoTitle = computed(() => project.value?.seo_title || project.value?.title || "Проєкт")
const seoDescription = computed(() => project.value?.seo_description || project.value?.short_description || "")
const breadcrumbs = computed(() => [
  { label: "Головна", to: "/" },
  { label: "Проєкти", to: "/projects" },
  { label: project.value?.title || "Проєкт" },
])

if (!project.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Project not found",
  })
}

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  twitterTitle: () => seoTitle.value,
  twitterDescription: () => seoDescription.value,
})

const resolveImageUrl = (url) => {
  if (!url) return ""
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith("/media/")) return url
  return url
}
</script>

<template>
  <section class="project-detail">
    <div class="project-detail__container">
      <Breadcrumbs :items="breadcrumbs" class="project-detail__breadcrumbs" />
      <h1 class="project-detail__title">{{ project.title }}</h1>
      <div class="project-detail__lead" v-html="project.short_description"></div>

      <div class="project-detail__tags">
        <span
          v-for="(tag, index) in project.tags"
          :key="`${project.slug}-${tag}`"
          class="project-detail__tag"
          :style="{ '--tag-delay': `${280 + index * 55}ms` }"
        >
          {{ tag }}
        </span>
      </div>

      <div class="project-detail__image-wrap">
        <img :src="resolveImageUrl(project.image_url)" :alt="project.title" class="project-detail__image">
      </div>

      <div class="project-detail__grid">
        <article class="project-detail__block">
          <div class="project-detail__block-content">
            <h3>Бізнес-проблема</h3>
            <div class="project-detail__block-text" v-html="project.detail_problem"></div>
          </div>
        </article>
        <article class="project-detail__block">
          <div class="project-detail__block-content">
            <h3>Рішення</h3>
            <div class="project-detail__block-text" v-html="project.detail_solution"></div>
          </div>
        </article>
        <article class="project-detail__block">
          <div class="project-detail__block-content">
            <h3>Результат</h3>
            <div class="project-detail__block-text" v-html="project.detail_result"></div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.project-detail {
  padding: 4.5em 1.25em 4em;
  // background-color: #FFFFFF;
}

.project-detail__container {
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.25em;
}

.project-detail__breadcrumbs,
.project-detail__title {
  animation: project-detail-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.project-detail__breadcrumbs {
  animation-delay: 0ms;
}

.project-detail__title {
  animation-delay: 80ms;
  font-size: clamp(1.75rem, 3vw, 2.75rem);
  line-height: 1.2;
}

.project-detail__lead {
  animation: project-detail-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 160ms both;
  color: #404040;
  line-height: 1.6;
}

.project-detail__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
}

.project-detail__tag {
  animation: project-detail-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) var(--tag-delay, 280ms) both;
  border-radius: 999px;
  background: #FFFFFF;
  // border: 1px solid #d2d2d2;
  padding: 0.42em 0.8em;
  font-size: 0.85rem;
  font-weight: 600;
}

.project-detail__image-wrap {
  animation: project-detail-fade-up 0.75s cubic-bezier(0.22, 1, 0.36, 1) 340ms both;
  border-radius: 1em;
  border: 1px solid #e7e7e7;
  overflow: hidden;
  aspect-ratio: 2.2 / 1;
  max-height: 420px;
  background: #f5f5f5;
}

.project-detail__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: project-detail-image-in 1s cubic-bezier(0.22, 1, 0.36, 1) 340ms both;
}

.project-detail__grid {
  border-top: 1px solid #dfdfdf;
  display: flex;
  flex-direction: column;
  margin-top: 1.75em;
}

.project-detail__block {
  align-items: start;
  animation: project-detail-fade-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  border-bottom: 1px solid #dfdfdf;
  display: grid;
  gap: clamp(2em, 7vw, 6em);
  grid-template-columns: minmax(9em, 13.5em) minmax(0, 1fr);
  padding: 3em 0;
}

.project-detail__block:nth-child(1) {
  animation-delay: 480ms;
}

.project-detail__block:nth-child(2) {
  animation-delay: 600ms;
}

.project-detail__block:nth-child(3) {
  animation-delay: 720ms;
}

.project-detail__block-content {
  display: contents;
  min-width: 0;
}

.project-detail__block h3 {
  color: #0f0f0f;
  font-size: clamp(1.1rem, 1.8vw, 1.35rem);
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.project-detail__block-text {
  color: #2f2f2f;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
  max-width: 42em;
}

.project-detail__block-text :deep(*) {
  margin-bottom: 0.6em;
}

.project-detail__block-text :deep(*:last-child) {
  margin-bottom: 0;
}

@keyframes project-detail-fade-up {
  from {
    opacity: 0;
    transform: translate3d(0, 1.25em, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes project-detail-image-in {
  from {
    transform: scale(1.035);
  }

  to {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .project-detail__breadcrumbs,
  .project-detail__title,
  .project-detail__lead,
  .project-detail__tag,
  .project-detail__image-wrap,
  .project-detail__image,
  .project-detail__block {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

@media screen and (max-width: 767px) {
  .project-detail {
    padding-top: 3.5em;
  }

  .project-detail__image-wrap {
    aspect-ratio: 2.2 / 1;
    max-height: 300px;
  }

  .project-detail__grid {
    margin-top: 1em;
  }

  .project-detail__block {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    padding: 1.5em 0;
  }

  .project-detail__block h3 {
    font-size: 1.15rem;
  }

  .project-detail__block-text {
    font-size: 0.96rem;
  }
}
</style>
